import { YStack, XStack, Text, View } from "tamagui";
import { colors } from "@styles/colors";
import { Header, Fade, RADIUS } from "./Common";
import { Ar } from "./Icons";
import RadiusCard from "@components/layout/RadiusCard";
import { RiMapPinLine, RiCoupon2Line, RiParentLine, RiInformationLine } from "react-icons/ri";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";

export default function StepChoose({
  onNew,
  onJoin,
  onAddExisting,
  onBack,
}: {
  onNew: () => void;
  onJoin: () => void;
  onAddExisting: () => void;
  onBack: () => void;
}) {
  // 暫時隱藏「地址配對新增」入口（及其說明框）。流程邏輯尚未實作，先不開放；
  // 日後要恢復把此旗標改為 true 即可（StepAddress / StepMatching / StepSelect 仍保留）。
  const SHOW_ADDRESS_MATCHING = false;
  return (
    <RadiusContentWrapper padding="0" space={0} background="transparent">
      <Header title="新增瓦斯帳戶" sub="請選擇適合您的方式來開始" onBack={onBack} />
      <YStack p="$4" gap="$2.5">
        {SHOW_ADDRESS_MATCHING && (
          <Fade i={0}>
            <YStack onPress={onNew} cursor="pointer">
              <RadiusCard>
                <XStack ai="flex-start" gap="$3.5">
                  <View w={50} h={50} br={13} bg={colors.primaryActive} ai="center" jc="center" flexShrink={0}>
                    <RiMapPinLine color={colors.primary} size={24} />
                  </View>
                  <YStack f={1} pr="$6">
                    <Text fontSize={15} fontWeight="700" mb="$1">
                      地址配對新增
                    </Text>
                    <Text fontSize={12} color={colors.gray} lineHeight={20}>
                      填送貨地址，自動找出能配送的瓦斯行。下單後瓦斯行主動完成開戶。
                    </Text>
                  </YStack>
                </XStack>
              </RadiusCard>
            </YStack>
          </Fade>
        )}

        <Fade i={1}>
          <YStack onPress={onAddExisting} cursor="pointer">
            <RadiusCard>
              <XStack ai="center" gap="$3.5">
                <View w={50} h={50} br={13} bg={colors.bgGreen} ai="center" jc="center" flexShrink={0}>
                  <RiCoupon2Line color={colors.green} size={24} />
                </View>
                <YStack f={1}>
                  <Text fontSize={15} fontWeight="700" mb="$1">
                    客戶序號配對新增
                  </Text>
                  <Text fontSize={12} color={colors.gray} lineHeight={18}>
                    輸入瓦斯行提供的客戶序號，立即綁定現有帳戶
                  </Text>
                </YStack>
                <Ar />
              </XStack>
            </RadiusCard>
          </YStack>
        </Fade>

        <Fade i={2}>
          <YStack onPress={onJoin} cursor="pointer">
            <RadiusCard>
              <XStack ai="center" gap="$3.5">
                <View w={50} h={50} br={13} bg={colors.bgAmber} ai="center" jc="center" flexShrink={0}>
                  <RiParentLine color={colors.amber} size={24} />
                </View>
                <YStack f={1}>
                  <Text fontSize={15} fontWeight="700" mb="$1">
                    加入既有的瓦斯帳戶
                  </Text>
                  <Text fontSize={12} color={colors.gray} lineHeight={18}>
                    輸入 6 位邀請碼或掃描 QR Code，共同管理現有瓦斯帳戶
                  </Text>
                </YStack>
                <Ar />
              </XStack>
            </RadiusCard>
          </YStack>
        </Fade>

        {SHOW_ADDRESS_MATCHING && (
          <Fade i={3}>
            <YStack bg={colors.primaryActive} br={RADIUS} p="$4">
              <XStack ai="center" gap="$2" mb="$3">
                <RiInformationLine color={colors.primary} size={16} />
                <Text fontSize={12} fontWeight="700" color={colors.primary}>
                  建立新帳號的流程
                </Text>
              </XStack>
              <YStack gap="$2">
                {[
                  ["1", "輸入地址", "告訴我們要送到哪"],
                  ["2", "自動配對", "找出附近能配送的瓦斯行"],
                  ["3", "選一家下單", "挑一家直接訂購"],
                  ["4", "完成開戶", "送貨員到府時順便建檔"],
                ].map(([n, t, s]) => (
                  <XStack key={n} ai="flex-start" gap="$2.5">
                    <View w={19} h={19} br={10} bg={colors.primary} ai="center" jc="center" flexShrink={0} mt={1}>
                      <Text fontSize={10} fontWeight="700" color="#fff">
                        {n}
                      </Text>
                    </View>
                    <XStack ai="center" gap="$1.5">
                      <Text fontSize={12} fontWeight="700" color={colors.primary}>
                        {t}
                      </Text>
                      <Text fontSize={11} color={colors.primary} opacity={0.75}>
                        {s}
                      </Text>
                    </XStack>
                  </XStack>
                ))}
              </YStack>
            </YStack>
          </Fade>
        )}
        {/* 只有兩個選項時整頁下半是空的，客戶也常分不清這兩者的差別——
            把「怎麼分辨」直接寫在下面，既填了版面也是真的有用的內容。 */}
        <Fade i={3}>
          <YStack bg={colors.primaryActive} br={RADIUS} p="$4" mt="$2">
            <XStack ai="center" gap="$2" mb="$3">
              <RiInformationLine color={colors.primary} size={16} />
              <Text fontSize={12.5} fontWeight="700" color={colors.primary}>
                不確定選哪一個？
              </Text>
            </XStack>
            <YStack gap="$2.5">
              {[
                ["客戶序號", "你自己已經是這家瓦斯行的客戶，序號印在帳單上或可向瓦斯行索取。"],
                ["邀請碼", "家人或同事已經有帳戶，請他們在帳戶頁點「邀請成員」產生 6 位數碼給你。"],
              ].map(([t, d]) => (
                <YStack key={t} gap={2}>
                  <Text fontSize={12} fontWeight="700" color={colors.primary}>
                    {t}
                  </Text>
                  <Text fontSize={11.5} color={colors.primary} opacity={0.78} lineHeight={18}>
                    {d}
                  </Text>
                </YStack>
              ))}
            </YStack>
          </YStack>
        </Fade>
      </YStack>
    </RadiusContentWrapper>
  );
}
